import React, { useState } from 'react'
import { LuArrowLeft, LuTrash2, LuX } from 'react-icons/lu'

import { useTagEdit } from '@/feature/tags/hooks/useTagEdit'
import { useTagDelete } from '@/feature/tags/hooks/useTagDelete'
import { useTagsStore } from '@/feature/tags/store'

import { ColorPicker } from '../ColorPicker'
import styles from './TagsPopout.module.css'
import { EditTagScreenProps } from './TagsPopout.types'

export const EditTagScreen: React.FC<EditTagScreenProps> = ({
  tag,
  onBack,
  onTagUpdated,
  onTagDeleted,
}) => {
  const [tagName, setTagName] = useState(tag.name)
  const [selectedColor, setSelectedColor] = useState(tag.color || '#FFFFFF')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const { activeAssetData, setActiveAssetData } = useTagsStore()

  const { updateTag, isEditing, validationError } = useTagEdit({
    onSuccess: (updatedTag) => {
      // Update active asset data if it contains this tag
      if (activeAssetData?.tags?.some((t) => t.id === tag.id)) {
        const updatedAssetTags = activeAssetData.tags.map((t) =>
          t.id === tag.id ? { ...t, name: updatedTag.name, color: updatedTag.color } : t,
        )
        setActiveAssetData({ ...activeAssetData, tags: updatedAssetTags })
      }

      onTagUpdated(updatedTag)
    },
    onError: (error) => {
      console.error('Error updating tag:', error)
    },
  })

  const { removeTag, isDeleting } = useTagDelete({
    onSuccess: () => {
      // Update active asset data if it contains this tag
      if (activeAssetData?.tags?.some((t) => t.id === tag.id)) {
        const updatedAssetTags = activeAssetData.tags.filter((t) => t.id !== tag.id)
        setActiveAssetData({ ...activeAssetData, tags: updatedAssetTags })
      }

      onTagDeleted(tag.id)
    },
    onError: (error) => {
      console.error('Error deleting tag:', error)
    },
  })

  const handleUpdateTag = async () => {
    if (!tagName.trim()) return

    // Only update if something has changed
    const nameChanged = tagName !== tag.name
    const colorChanged = selectedColor !== tag.color

    if (!nameChanged && !colorChanged) {
      onBack()
      return
    }

    await updateTag(tag.id, tagName, selectedColor)
  }

  const handleDeleteTag = async () => {
    await removeTag(tag.id)
  }

  const isLoading = isEditing || isDeleting

  return (
    <>
      <div className={styles.popoutHeader}>
        <button
          className={styles.backButton}
          onClick={(e) => {
            e.stopPropagation()
            onBack()
          }}
          disabled={isLoading}
          data-testid="back-button"
        >
          <LuArrowLeft size={16} />
        </button>
        <div className={styles.headerTitle} data-testid="header-title">
          Edit Tag
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.actionButton}
            onClick={(e) => {
              e.stopPropagation()
              onBack()
            }}
            disabled={isLoading}
            data-testid="close-button"
          >
            <LuX size={16} />
          </button>
        </div>
      </div>

      <div className={styles.createTagContent}>
        <div className={styles.inputGroup}>
          {validationError && <div className={styles.errorLine}>{validationError}</div>}
          <input
            id="tagName"
            type="text"
            value={tagName}
            onChange={(e) => setTagName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleUpdateTag()
              }
            }}
            placeholder="Enter tag name"
            className={styles.tagNameInput}
            data-testid="tag-name-input"
            autoFocus
            disabled={isLoading}
          />
        </div>

        <ColorPicker
          value={selectedColor}
          onChange={setSelectedColor}
          disabled={isLoading}
          dataTestId="color-picker"
        />
      </div>

      <div className={styles.buttonGroup}>
        <button
          className={styles.updateButton}
          onClick={handleUpdateTag}
          disabled={!tagName.trim() || isLoading}
          data-testid="update-button"
        >
          {isEditing ? 'Updating...' : 'Update Tag'}
        </button>
        <button
          className={styles.deleteButton}
          onClick={(e) => {
            e.stopPropagation()
            setShowDeleteConfirm(true)
          }}
          disabled={isLoading}
          title="Delete tag"
          data-testid="delete-button"
        >
          <LuTrash2 size={16} />
        </button>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className={styles.deleteConfirmOverlay} data-testid="delete-confirm-overlay">
          <div className={styles.deleteConfirmDialog} data-testid="delete-confirm-dialog">
            <h3>Delete Tag</h3>
            <p data-testid="delete-confirm-message">
              Are you sure you want to delete &quot;{tag.name}&quot;?
            </p>
            <p>This will remove the tag from all other entities.</p>
            <p>This action cannot be undone.</p>
            <div className={styles.deleteConfirmActions}>
              <button
                className={styles.cancelButton}
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                data-testid="cancel-delete-button"
              >
                Cancel
              </button>
              <button
                className={styles.deleteButton}
                onClick={() => {
                  setShowDeleteConfirm(false)
                  handleDeleteTag()
                }}
                disabled={isDeleting}
                data-testid="confirm-delete-button"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
